import { X } from 'lucide-react';
import React from 'react';

import { Progress } from '@/components/ui/progress';
import { useGetProgressQuery } from '@/services/api/demandProgressService';
import {
  useCancelGenerationMutation,
  useGetDemandGenerationStatusQuery,
} from '@/services/api/demandService';

interface InlineDemandGenerationOverlayProps {
  matterId: string;
  threadId?: string | null;
  children: React.ReactNode;
  className?: string;
  mode?: 'fixed' | 'inline';
}

/**
 * Displays a spinner while the DemandGenerationFromTemplateWorkflow is RUNNING.
 * When the workflow status is anything else, renders children normally.
 */
export const InlineDemandGenerationOverlay: React.FC<
  InlineDemandGenerationOverlayProps
> = ({ matterId, threadId, children, className, mode = 'fixed' }) => {
  const { data, isLoading } = useGetDemandGenerationStatusQuery(
    { matterId, threadId: threadId ?? undefined },
    {
      // Poll every 5 seconds. No skip needed because backend will auto-detect
      // the system thread when `threadId` is undefined.
      pollingInterval: 500,
    },
  );

  // Always fetch detailed progress to power inline progress bar when needed
  const { data: progressData } = useGetProgressQuery(matterId, {
    pollingInterval: 1000,
  });

  const [cancelGeneration, { isLoading: isCancelling }] =
    useCancelGenerationMutation();

  const hasOngoingProgress =
    progressData &&
    progressData.total > 0 &&
    progressData.done > 0 &&
    progressData.done < progressData.total;

  const showProgress =
    isLoading || data?.status === 'RUNNING' || hasOngoingProgress;

  const handleCancel = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isCancelling) {
      cancelGeneration({ matterId });
    }
  };

  if (mode === 'inline') {
    return (
      <div
        className={`relative inline-block ${className || ''} ${
          showProgress ? 'pointer-events-none' : ''
        }`}
      >
        {/* Keep button space but hide visually and disable interaction when running */}
        <span className={showProgress ? 'invisible' : 'visible'}>
          {children}
        </span>

        {showProgress && (
          <div className="absolute inset-0 z-0 flex items-center justify-center bg-white/70 backdrop-blur-sm rounded">
            {/* Render progress bar if we have total/done values, else generic spinner bar */}
            {progressData && progressData.total > 0 ? (
              <>
                <img
                  src="/progress.gif"
                  alt="Generating…"
                  className="h-8 w-8 mr-2"
                />
                <Progress
                  value={(progressData.done / progressData.total) * 100}
                  className="w-24 h-2"
                />
              </>
            ) : (
              <div className="h-2 w-24 bg-primary/20 rounded-full overflow-hidden">
                <div className="w-1/2 h-full bg-primary animate-pulse" />
              </div>
            )}
            <button
              type="button"
              onClick={handleCancel}
              disabled={isCancelling}
              className="pointer-events-auto ml-2 p-0.5 rounded-full hover:bg-red-100 text-gray-500 hover:text-red-600 transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
              title="Cancel generation"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        )}
      </div>
    );
  }

  // For non-inline mode, simply render children (no global bar desired)
  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{children}</>;
};

// Deprecated alias – remove after migration
export const DemandGenerationSpinner = InlineDemandGenerationOverlay;
